import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack, IconButton, Typography, Button } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { formatValue } from '../utils/string'
import { AuroraSetMinFlowLimitActionInput } from '../types/AuroraAwhpActionInput'
import PinCodeDialog from './PinCodeDialog'
import useDialog from '../hooks/useDialog'
import { AuroraAwhpActionNack } from '../errors'
import AuroraControl from './AuroraControl'

export interface AuroraMinFlowLimitControlProps {
  value: number | null
  loading?: boolean
  disabled?: boolean
  onSubmit: (input: AuroraSetMinFlowLimitActionInput) => Promise<void>
}

export default function AuroraMinFlowLimitControl({
  value,
  loading = false,
  disabled = false,
  onSubmit
}: AuroraMinFlowLimitControlProps) {
  const { t } = useTranslation('aurora')

  const { open, openDialog, closeDialog } = useDialog()

  const [minFlowLimit, setMinFlowLimit] = useState<number | null>(null)

  function handleIncrement() {
    setMinFlowLimit((prev) => {
      const current = prev !== null ? prev : value
      if (current !== null) {
        return Math.round(current) + 1
      }
      return null
    })
  }

  function handleDecrement() {
    setMinFlowLimit((prev) => {
      const current = prev !== null ? prev : value
      if (current !== null) {
        return Math.round(current) - 1
      }
      return null
    })
  }

  async function handleSubmit(pin: number) {
    closeDialog()

    if (minFlowLimit === null) {
      return
    }

    try {
      await onSubmit({
        alarms: {
          pin,
          minFlowLimit
        }
      })

      setMinFlowLimit(null)
    } catch (error) {
      if (error instanceof AuroraAwhpActionNack) {
        switch (error.message) {
          case 'PIN_MISSING':
          case 'PIN_REJECTED':
            openDialog()
            break
        }
      }
    }
  }

  function handleReset() {
    setMinFlowLimit(null)
  }

  return (
    <>
      <AuroraControl
        direction="column"
        label={t('minFlowLimit')}
        loading={loading}
        disabled={disabled}
        control={
          <Stack direction="column" justifyContent="center" alignItems="center" gap={2} pt={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={3}>
              <IconButton
                color={minFlowLimit !== null ? 'primary' : 'inherit'}
                disabled={loading || disabled}
                size="small"
                sx={{ border: '1px solid' }}
                onClick={handleDecrement}
              >
                <Remove />
              </IconButton>

              {minFlowLimit !== null ? (
                <Typography variant="h2" color={loading ? 'text.disabled' : 'primary'}>
                  {`${formatValue(minFlowLimit, 0)} m³/h`}
                </Typography>
              ) : (
                <Typography variant="h2" color="text.primary">
                  {`${formatValue(value, 0)} m³/h`}
                </Typography>
              )}

              <IconButton
                color={minFlowLimit !== null ? 'primary' : 'inherit'}
                disabled={loading || disabled}
                size="small"
                sx={{
                  border: '1px solid'
                }}
                onClick={handleIncrement}
              >
                <Add />
              </IconButton>
            </Stack>

            {minFlowLimit !== null && (
              <Stack direction="row" alignItems="center" gap={2}>
                <LoadingButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  loading={loading}
                  disabled={disabled}
                  onClick={openDialog}
                >
                  {t('actions:confirm')}
                </LoadingButton>

                {!loading && (
                  <Button
                    variant="text"
                    color="inherit"
                    size="small"
                    disabled={disabled}
                    onClick={handleReset}
                  >
                    {t('actions:cancel')}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        }
      />

      <PinCodeDialog open={open} onClose={closeDialog} onSubmit={handleSubmit} />
    </>
  )
}
